import React from "react";
import {
     Box,
     Button,
     DialogContentText,
     MenuItem,
     Select,
     CircularProgress,
} from "@mui/material";
import { IUser } from "../../../../api/homesites/homesites.types";
import { thinDarkScrollbar } from "../../../../utils/scrollbarStyles";
import { BaseDialog } from "./BaseDialog";
import { CommunityBreakdown } from "./CommunityBreakdown";
import { ConciergeCommunitiesList } from "./ConciergeCommunitiesList";

interface BulkAssignConciergeDialogProps {
     open: boolean;
     selectedBulkConcierge: IUser | null;
     selectedBulkCommunity: string;
     availableCommunities: string[];
     sortedConcierges: IUser[];
     communityConciergeBreakdown: any;
     conciergeCommunities: Array<{ community: string; assigned: number; available: number }>;
     hasHomesitesToUpdate: boolean;
     hasChanges: boolean;
     isBulkAssigning: boolean;
     userInfo: any;
     onClose: () => void;
     onConciergeChange: (conciergeName: string) => void;
     onCommunityChange: (community: string) => void;
     onConfirm: () => void;
     onCancel: () => void;
     onReassignClick: (communityName: string) => void;
     onUnassignClick: (communityName: string) => void;
}

export const BulkAssignConciergeDialog: React.FC<BulkAssignConciergeDialogProps> = ({
     open,
     selectedBulkConcierge,
     selectedBulkCommunity,
     availableCommunities,
     sortedConcierges,
     communityConciergeBreakdown,
     conciergeCommunities,
     hasHomesitesToUpdate,
     hasChanges,
     isBulkAssigning,
     userInfo,
     onClose,
     onConciergeChange,
     onCommunityChange,
     onConfirm,
     onCancel,
     onReassignClick,
     onUnassignClick,
}) => {
     const actions = (
          <>
               <Button
                    onClick={onCancel}
                    color="primary"
                    variant="outlined"
                    disabled={isBulkAssigning || !hasChanges}
                    style={{ textTransform: "none", border: "2px solid #005DAA" }}
               >
                    Cancel
               </Button>
               <Button
                    onClick={onConfirm}
                    color="primary"
                    variant="contained"
                    disabled={!selectedBulkConcierge || !selectedBulkCommunity || isBulkAssigning || !hasHomesitesToUpdate}
                    style={{ textTransform: "none" }}
               >
                    Assign
               </Button>
          </>
     );

     return (
          <BaseDialog
               open={open}
               title={`Community Assignment for ${selectedBulkCommunity}`}
               maxWidth="md"
               fullWidth
               showCloseButton
               showDivider
               disabled={isBulkAssigning}
               onClose={onClose}
               actions={actions}
          >
               {/* Community breakdown section - shown above dropdowns */}
               {selectedBulkCommunity && (
                    <>
                         {/* Show concierge breakdown for selected community */}
                         {communityConciergeBreakdown && (
                              <CommunityBreakdown communityConciergeBreakdown={communityConciergeBreakdown} />
                         )}
                    </>
               )}

               {/* Assignments section header */}
               <Box sx={{ marginTop: "16px", marginBottom: "12px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                         <DialogContentText
                              style={{
                                   fontSize: "14px",
                                   color: "#333",
                                   fontWeight: "600"
                              }}
                         >
                              Assign Concierge to Community{selectedBulkCommunity ? ` ${selectedBulkCommunity}` : ""}
                         </DialogContentText>
                         {selectedBulkCommunity && communityConciergeBreakdown && (
                              <DialogContentText
                                   style={{
                                        fontSize: "13px",
                                        color: "#666",
                                        fontStyle: "italic"
                                   }}
                              >
                                   {communityConciergeBreakdown.unassignedCount} Available, {communityConciergeBreakdown.concierges.reduce((sum: number, c: any) => sum + c.count, 0)} Assigned
                              </DialogContentText>
                         )}
                    </Box>
                    {/* Horizontal divider bar */}
                    <Box
                         sx={{
                              width: "100%",
                              height: "2px",
                              backgroundColor: "#e0e0e0",
                              marginBottom: "12px",
                              borderRadius: "1px"
                         }}
                    />
               </Box>

               <div style={{ marginBottom: "16px" }}>
                    <DialogContentText style={{ marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                         Community
                    </DialogContentText>
                    <Select
                         value={selectedBulkCommunity}
                         onChange={(e) => onCommunityChange(e.target.value)}
                         displayEmpty
                         fullWidth
                         disabled={isBulkAssigning}
                         renderValue={(selected) => {
                              if (!selected) {
                                   return <span style={{ color: "#5B7282", fontWeight: "lighter" }}>All Communities</span>;
                              }
                              return selected;
                         }}
                         MenuProps={{
                              PaperProps: {
                                   sx: {
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        ...thinDarkScrollbar,
                                   },
                              },
                         }}
                    >
                         <MenuItem value="">
                              <em>All Communities</em>
                         </MenuItem>
                         {availableCommunities.map((comm) => (
                              <MenuItem key={comm} value={comm}>
                                   {comm}
                              </MenuItem>
                         ))}
                    </Select>
               </div>

               <div>
                    <DialogContentText style={{ marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                         Concierge
                    </DialogContentText>
                    <Select
                         value={selectedBulkConcierge?.name || ""}
                         onChange={(e) => onConciergeChange(e.target.value)}
                         displayEmpty
                         fullWidth
                         disabled={isBulkAssigning}
                         renderValue={(selected) => {
                              if (!selected) {
                                   return <span style={{ color: "#5B7282", fontWeight: "lighter" }}>Select a concierge</span>;
                              }
                              return selected;
                         }}
                         MenuProps={{
                              PaperProps: {
                                   sx: {
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        ...thinDarkScrollbar,
                                   },
                              },
                         }}
                    >
                         {sortedConcierges.map((c) => (
                              <MenuItem key={c.email} value={c.name}>
                                   {c.name} {c.email === userInfo?.email && "(You)"}
                              </MenuItem>
                         ))}
                    </Select>
               </div>

               {/* Show communities where selected concierge is assigned */}
               <ConciergeCommunitiesList
                    conciergeCommunities={conciergeCommunities}
                    selectedBulkConcierge={selectedBulkConcierge}
                    userInfo={userInfo}
                    isBulkAssigning={isBulkAssigning}
                    onReassignClick={onReassignClick}
                    onUnassignClick={onUnassignClick}
               />

               {isBulkAssigning && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2 }}>
                         <CircularProgress size={20} />
                         <DialogContentText>
                              Assigning homesites... Please wait.
                         </DialogContentText>
                    </Box>
               )}
          </BaseDialog>
     );
};
